import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Download } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { downloadFile, request } from "@/lib/api";

const REQUEST_TYPES = [
  { value: "booking", label: "Health check" },
  { value: "booking_change_request", label: "Reschedule/cancel" },
  { value: "complimentary_booking", label: "Complimentary" },
  { value: "consultation", label: "Consultation" },
  { value: "offer_claim", label: "Offer claim" },
  { value: "lab_order", label: "Lab tests" },
];
// Types whose record is not a health-check booking, so the appointment-letter
// endpoint has nothing to generate for them.
const NO_APPOINTMENT_LETTER = ["offer_claim", "consultation", "lab_order"];
const PAGE_SIZE = 25;

// Detail records are not all flat: a lab order carries its patient as an
// object and its tests as a list, and JSON.stringify makes a dozen priced
// tests unreadable at exactly the moment ops needs to read them.
function formatFieldValue(value) {
  if (Array.isArray(value)) {
    return value.map((entry) => (entry && typeof entry === "object" ? entry.name ?? JSON.stringify(entry) : String(entry))).join(", ");
  }
  if (value && typeof value === "object") {
    return Object.entries(value).map(([key, inner]) => `${key.replaceAll("_", " ")}: ${inner}`).join(" · ");
  }
  return String(value);
}

/** Everything employees have asked for, across the organisation. */
export default function RequestsSection({ token, activeClientId }) {
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [feed, setFeed] = useState({ items: [], total: 0 });
  const [detail, setDetail] = useState(null);
  const [detailNotice, setDetailNotice] = useState("");
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!activeClientId) return;
    const params = new URLSearchParams({ client_id: activeClientId, page: String(page), page_size: String(PAGE_SIZE) });
    if (typeFilter) params.set("type", typeFilter);
    if (statusFilter) params.set("status", statusFilter);
    try {
      setFeed(await request(`/admin/requests?${params.toString()}`, {}, token));
    } catch (loadError) {
      setError(loadError.message);
    }
  }, [activeClientId, typeFilter, statusFilter, page, token]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [activeClientId]);

  async function openDetail(item) {
    try {
      const record = await request(`/admin/requests/${item.type}/${item.id}`, {}, token);
      setDetailNotice("");
      setDetail({ ...item, record });
    } catch (detailError) {
      setError(detailError.message);
    }
  }

  // Approving a reschedule/cancel already existed as an endpoint but had no
  // control in the UI, so ops could see a request and not act on it.
  async function resolveChangeRequest() {
    if (!detail) return;
    setResolving(true);
    setError("");
    try {
      await request(`/admin/requests/booking_change_request/${detail.id}/resolve`, { method: "POST" }, token);
      setDetailNotice("Approved. Apollo has been updated where the booking is linked, and the employee has been emailed.");
      await load();
    } catch (resolveError) {
      setError(resolveError.message);
    } finally {
      setResolving(false);
    }
  }

  async function downloadLetter(bookingId) {
    setError("");
    try {
      await downloadFile(`/admin/bookings/${bookingId}/appointment-letter/download`, `appointment-letter-${bookingId}.pdf`, token);
    } catch (letterError) {
      setError(letterError.message);
    }
  }

  // A lab order's letter doubles as the picking list for the collection round,
  // which is the thing ops actually has to arrange by hand.
  async function downloadLabLetter(orderId) {
    setError("");
    try {
      await downloadFile(`/admin/lab-orders/${orderId}/collection-letter/download`, `lab-order-${orderId}.pdf`, token);
    } catch (letterError) {
      setError(letterError.message);
    }
  }

  const totalPages = Math.max(1, Math.ceil(feed.total / PAGE_SIZE));

  return (
    <section className="ops-panel" data-testid="ops-requests">
      <header className="ops-panel__head">
        <div>
          <h1>Requests</h1>
          <p>{feed.total} from this organisation</p>
        </div>
      </header>

      {error && <p className="form-error" role="alert" data-testid="admin-dashboard-error">{error}</p>}

      <div className="admin-filters">
        <Select value={typeFilter || "all"} onValueChange={(value) => { setTypeFilter(value === "all" ? "" : value); setPage(1); }}>
          <SelectTrigger className="w-[220px]" data-testid="admin-type-filter"><SelectValue placeholder="All request types" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All request types</SelectItem>{REQUEST_TYPES.map((item) => <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={statusFilter || "all"} onValueChange={(value) => { setStatusFilter(value === "all" ? "" : value); setPage(1); }}>
          <SelectTrigger className="w-[180px]" data-testid="admin-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{[...new Set(feed.items.map((item) => item.status))].map((status) => <SelectItem key={status} value={status}>{status.replaceAll("_", " ")}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="ops-table-wrap">
        <Table data-testid="admin-requests-table">
          <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Employee</TableHead><TableHead>Summary</TableHead><TableHead>Status</TableHead><TableHead>Submitted</TableHead></TableRow></TableHeader>
          <TableBody>
            {feed.items.map((item) => <TableRow key={`${item.type}-${item.id}`} className="admin-request-row" onClick={() => openDetail(item)} data-testid={`admin-request-row-${item.type}-${item.id}`}>
              <TableCell><Badge variant="outline">{item.type.replaceAll("_", " ")}</Badge></TableCell>
              <TableCell>{item.employee_name || item.employee_email}</TableCell>
              <TableCell>{item.summary}</TableCell>
              <TableCell><Badge>{item.status.replaceAll("_", " ")}</Badge></TableCell>
              <TableCell>{new Date(item.submitted_at).toLocaleString("en-IN")}</TableCell>
            </TableRow>)}
            {!feed.items.length && <TableRow><TableCell colSpan={5} data-testid="admin-empty-requests">No requests match these filters.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

        <div className="admin-pagination">
          <Button variant="outline" disabled={page <= 1} onClick={() => setPage((current) => current - 1)} data-testid="admin-prev-page-button">Previous</Button>
          <span data-testid="admin-page-indicator">Page {page} of {totalPages}</span>
          <Button variant="outline" disabled={page >= totalPages} onClick={() => setPage((current) => current + 1)} data-testid="admin-next-page-button">Next</Button>
        </div>

      <Dialog open={Boolean(detail)} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent data-testid="admin-request-detail-dialog">
          <DialogHeader><DialogTitle>{detail?.summary}</DialogTitle></DialogHeader>
          <div className="admin-detail-fields">{detail?.record && Object.entries(detail.record).map(([key, value]) => <div className="review-row" key={key}><span>{key.replaceAll("_", " ")}</span><strong>{formatFieldValue(value)}</strong></div>)}</div>
          {detailNotice && <p className="profile-saved" role="status" data-testid="admin-detail-notice"><CheckCircle2 size={15} />{detailNotice}</p>}
          <div className="admin-detail-actions">
            {/* The letter ops gets asked for when someone loses theirs or a
                centre wants it re-sent. */}
            {detail?.type === "lab_order" && (
              <Button variant="outline" onClick={() => downloadLabLetter(detail.id)} data-testid="admin-download-lab-letter-button">
                <Download size={16} />Collection letter
              </Button>
            )}
            {(detail?.record?.booking_id || detail?.id) && !NO_APPOINTMENT_LETTER.includes(detail?.type) && (
              <Button variant="outline" onClick={() => downloadLetter(detail.record?.booking_id || detail.id)} data-testid="admin-download-letter-button">
                <Download size={16} />Appointment letter
              </Button>
            )}
            {detail?.type === "booking_change_request" && detail?.record?.status === "pending_provider_confirmation" && (
              <Button onClick={resolveChangeRequest} disabled={resolving} data-testid="admin-resolve-request-button">
                <CheckCircle2 size={16} />{resolving ? "Approving…" : `Approve ${detail.record?.request_type || "request"}`}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
